import React from 'react';
import PropTypes from 'prop-types';

import { Alert, Box, Button, Container } from '@mui/joy';
import { TextField, ToggleField } from '@/components/InputField';

import i18n from '@/i18n';
import { useState } from 'react';
import { AudioField } from '@/components/AudioField';

import { SelectRelatedNodes } from '@/components/SelectRelatedNodes';

import { SelectRelatedNode } from '@/components/SelectRelatedNode';
import { Loading } from '@/components/Loading';
import { useParams } from 'react-router-dom';
import { Check, Lock } from '@phosphor-icons/react';

import { useHasFeatureFlag, useLocalStorage, useResource } from '@/hooks';
import { useEffect } from 'react';
import { ErrorView } from '@/components/ErrorView';
import { useSnackbar } from 'notistack';
import { uploadAudioToRecording } from '@/actions/login';
import { Typography } from '@mui/material';
import { isValidISRC } from '@/utils';
import { FeatureCard } from '@/components/FeatureCard';
import { ISRCField } from '@/components/ISRCField';
import { useManager } from '@/hooks';
import { updateRecording } from '@/actions/aloader';
import { PlayButton } from '@/components/PlayButton';
import LoadingPage from '@/pages/loading';
import ErrorPage from '@/pages/error';
import { useMutation } from '@tanstack/react-query';

export function EditRecordingPage() {
  const { recordingId } = useParams()
  return (
    <React.Suspense fallback={<Loading />}>
      <EditRecordingView recordingId={recordingId} />
    </React.Suspense>
  )
}

export function RecordingForm({
  values: defaultValues,
  recording,
  saving,
  submitLabel,
  onSubmit = () => console.warn('onSubmit not implemented'),
  onValuesChange = () => console.warn('onValuesChange not implemented')
}) {
  const manager = useManager()
  const [values, setValues] = useLocalStorage('recordingValues', defaultValues)

  const [errors, setErrors] = useState([])
  const [isrcError, setISRCError] = useState(null)

  const [isrcAlreadyExists, setISRCAlreadyExists] = useState(false)

  const hasAccessToPublicDomain = useHasFeatureFlag('studio_public_domain_toggle')

  const getFieldErrors = (fieldId) => errors?.filter(i => i.field === fieldId)

  const validateForm = () => {
    let errors = []
    
    if (isrcError) {
      errors.push(isrcError)
    }

    if (!values?.primary_genre) {
      errors.push({
        field: 'primary_genre',
        description: i18n.t('track-must-have-a-genre'),
        type: 'missing'
      })
    }

    if (!values?.audio && !values.audio_url) {
      errors.push({
        field: 'audio',
        description: i18n.t('track-must-have-audio'),
        type: 'missing'
      })
    }

    if (!values?.name) {
      errors.push({
        field: 'name',
        description: i18n.t('track-must-have-a-name'),
        type: i18n.t('empty')
      })
    }

    if (values.name?.toLowerCase()?.indexOf('feat.') !== -1) {
      errors.push({
        field: 'name',
        description: i18n.t('track-name-must-not-contain-feat'),
        type: i18n.t('empty')
      })
    }

    if (values.name?.toLowerCase()?.indexOf('featuring') !== -1) {
      errors.push({
        field: 'name',
        description: i18n.t('track-name-must-not-contain-feat'),
        type: i18n.t('empty')
      })
    }

    if (values?.isrc?.length > 0 && !isValidISRC(values.isrc)) {
      errors.push({
        field: 'isrc',
        description: i18n.t('invalid ISRC code'),
        type: i18n.t('invalid')
      })
    }
    if (!(values?.recording_contributors?.length > 0)) {
      errors.push({
        field: 'recording_contributors',
        description: i18n.t('missing-track-contributors'),
        type: 'missing'
      })
    }

    if (!(values?.recording_artists?.length > 0)) {
      errors.push({
        field: 'recording_artists',
        description: i18n.t('missing-track-artists'),
        type: 'missing'
      })
    }
    setErrors(errors)
    return errors?.length < 1
  }

  useEffect(() => {
    onValuesChange(values)
  }, [values])

  useEffect(() => {
    setValues({ ...recording })
  }, [recording])

  const handleSubmit = (event) => {
    event.preventDefault()
    if (validateForm()) {
      onSubmit(values)
    }
    return false    
  }

  return (
    <form method="POST" key={recording?.id} onSubmit={handleSubmit}>
      <Box sx={{ marginBottom: 1, gap: 1, display: 'flex', flexDirection: 'column' }}>
        {errors?.map(error => (
          <Alert key={error.description} color="danger">{error.description}</Alert>
        ))}

        {values?.locked && (
          <FeatureCard
            sx={{ marginTop: 2, p: 1 }}
            Icon={Lock}
            name={i18n.t('recording-locked.description')}
            description={i18n.t('recording-locked.heading')}
          /> 
        )}
        <AudioField
          readOnly={values?.locked}
          label={i18n.t('audio')}
          errors={getFieldErrors('audio')}
          audioUrl={values?.audio_url}
          hasAudio={values?.has_audio}
          renderPlayer={() => <PlayButton size={64} recording={recording} />}
          playable={values?.audio_url || !!(values?.audio)}
          onChange={(value) => setValues({ ...values, audio: value })}
        />
        <TextField
          readOnly={values?.locked}
          errors={getFieldErrors('name')}
          name="name"
          defaultValue={recording?.name}
          label={i18n.t('title')}
          placeholder={i18n.t("enter-track-title")}
          onChange={(event) => setValues({ ...values, [event.target.name]: event.target.value })}
        />
        <Typography sx={{ fontWeight: 'bold' }}>{i18n.t('track-name-field-info')}</Typography>
        <TextField
          readOnly={values?.locked}
          name="version"
          label={i18n.t('version')}
          defaultValue={recording?.version}
          placeholder={i18n.t("enter-track-version")}
          onChange={(event) => setValues({ ...values, [event.target.name]: event.target.value })}
        />
        <Typography sx={{ opacity: 0.5, marginBottom: 1 }}>For example: Remix or Radio edit</Typography>
        <ISRCField
          readOnly={values?.locked}
          label={i18n.t('isrc-optional')}
          name="isrc"
          errors={getFieldErrors('isrc')}
          defaultValue={recording?.isrc}
          placeholder={i18n.t("not-required-use-only-if-you-are-certain")}
          onChange={(event) => setValues({ ...values, [event.target.name]: event.target.value })}
          onISRCExistChange={(exist) => {
            setISRCAlreadyExists(exist)
          }}
          onError={(error) => {
            setISRCError(error)
            let newErrors = [...errors]
            newErrors = newErrors.filter(e => e.field !== 'isrc')

            if (error) {
              newErrors.push(error)
            }

            setErrors(newErrors)
          }}
        />
        {hasAccessToPublicDomain && (
          <ToggleField
            label={i18n.t('is-public-domain')}
            checked={values?.is_public_domain ?? false}
            onChange={(checked) => setValues({ ...values, is_public_domain: checked ?? false })}
          />
        )}
        <SelectRelatedNode
          readOnly={values?.locked}
          label={i18n.t('genre')}
          errors={getFieldErrors('primary_genre')}
          ownerNodeId={null}
          nodeType="genre"
          ownerNodeType="recording"
          addLabel={"enter-track-genre"}
          placeholder={i18n.t('enter-track-genre')}
          allLabel={i18n.t('all-genres')}
          ownLabel={i18n.t('track-s-genres')}
          node={values?.primary_genre}
          onChange={(primary_genre) => setValues({ ...values, primary_genre })}
        />
        <SelectRelatedNodes
          readOnly={values?.locked}
          label={i18n.t('contributors')}
          errors={getFieldErrors('recording_contributors')}
          ownerNodeId={null}
          nodeType="person"
          placeholder={i18n.t('add-track-contributors')}
          canCreate={true}
          createNodeColumns={[
            {
              label: i18n.t('name'),
              placeholder: i18n.t('name'),
              id: "name",
              type: 'text'
            }
          ]}
          ownerNodeType="recording"
          addLabel={"add-person"}
          junctionNodeColumns={[
            {
              placeholder: i18n.t('role'),
              id: "roles",
              type: 'belongsToMany',
              multiple: true,
              nodeType: 'roles'
            }
          ]}
          allLabel={i18n.t('all-people')}
          ownLabel={i18n.t('track-s-people')}
          nodes={values?.recording_contributors ?? []}
          onChange={(recording_contributors) => {
            setValues({ ...values, recording_contributors })
          }}
        />
        <SelectRelatedNodes
          readOnly={values?.locked}
          label={i18n.t('royalty-splits')}
          ownerNodeId={null}
          nodeType="payee"
          ownerNodeType="recording"
          placeholder={i18n.t('add-royalty-splits')}
          addLabel={i18n.t("add-royalty-split")}
          createLabel={i18n.t("create-beneficiary")}
          emptyText={i18n.t('no-beneficiaries-added')}
          canCreate={true}
          createNodeColumns={[
            {
              type: 'text',
              label: 'name',
              id: 'name'
            },
            {
              type: 'text',
              label: i18n.t('email'),
              id: 'email'
            }
          ]}
          columns={[
            {
              type: 'text',
              label: i18n.t('email'),
              id: 'email'
            }
          ]}
          junctionNodeColumns={[
            {
              type: 'percentage',
              label: i18n.t('percentage'),
              placeholder: i18n.t('0-100'),
              id: 'percentage'
            }
          ]}
          allLabel={i18n.t('all-beneficiaries')}
          ownLabel={i18n.t('track-beneficiaries')}
          nodes={values?.recording_payees ?? []}
          onChange={(recording_payees) => {
            setValues({ ...values, recording_payees })
          }}
        />
        <SelectRelatedNodes
          readOnly={values?.locked}
          errors={getFieldErrors('recording_artists')}
          placeholder={i18n.t('add-track-artists')}
          label={i18n.t('artists')}
          createNodeColumns={[
            {
              id: 'name',
              type: "text",
              fieldId: 'name',
              label: i18n.t('name')
            }
          ]}
          junctionNodeColumns={[
            /*{
              id: 'primary',
              type: 'bool',
              label: i18n.t('primary')
            }*/
          ]}
          canCreate={true}
          ownerNodeId={null}
          nodeType="artist"
          ownerNodeType="recording"
          addLabel={"add-artist"}
          allLabel={i18n.t('all-artists')}
          ownLabel={i18n.t('track-s-artists')}
          nodes={values?.recording_artists ?? []}
          onChange={(recording_artists) => setValues({ ...values, recording_artists })}
        />
        {manager?.type === "organization" && (
          <SelectRelatedNode
            readOnly={values?.locked}
            label={i18n.t('label')}
            ownerNodeId={null}
            nodeType="label"
            ownerNodeType="recording"
            addLabel={"add-label"}
            placeholder={i18n.t('selct-track-label')}
            allLabel={i18n.t('all-labels')}
            ownLabel={i18n.t('track-s-label')}
            node={values?.label}
            onChange={(label) => setValues({ ...values, label })}
          />
        )}
        <Button aria-label={submitLabel} sx={{ alignSelf: 'flex-start' }} loading={saving} type="submit" disabled={values?.locked || isrcAlreadyExists} startDecorator={<Check />} variant="solid">{submitLabel}</Button>
      </Box>
    </form>
  )
}

RecordingForm.propTypes = {
  values: PropTypes.object,
  recording: PropTypes.object,
  saving: PropTypes.bool,
  submitLabel: PropTypes.string,
  onSubmit: PropTypes.func,
  onValuesChange: PropTypes.func
}

export function EditRecordingView({
  recordingId
}) {
  const manager = useManager()
  const [saving, setSaving] = useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const [values, setValues] = useState(null)

  const { data: recording, isLoading, error, isError, refetch } = useResource({
    queryKey: [manager?.type, manager?.slug, 'recording', recordingId],
    path: `/${manager?.type}/${manager?.slug}/recording/${recordingId}`
  })

  const saveRecording = async (values) => {
    const update = await updateRecording(manager?.type, manager?.id, recordingId, values);
    if (values.audio) {
      const audio = await uploadAudioToRecording(recordingId, values.audio)
    }
  }

  const mutation = useMutation({
    mutationFn: saveRecording,
    onMutate: () => {
      setSaving(true)
    },
    onSettled: () => {
      setSaving(false)
      enqueueSnackbar(i18n.t('recording-saved'))
    },
    onError: () => {
      setSaving(false)
      enqueueSnackbar(i18n.t('error-saving-release'))
    }
  })

  const handleSubmit = (values) => { 
    mutation.mutate(values)

    return false
  }

  useEffect(() => {
    if (recording) {
      setValues({
        ...recording
      })
    }
  }, [recording])

  console.log(error, isError)

  if (error) {
    return (
      <ErrorPage onTryAgainClick={() => refetch()} />
    ) 
  }

  if (isLoading) {
    return (
      <LoadingPage />
    )
  }

  if (values && recording) {
    return (
      <Container>
        <h1>{i18n.t('edit-track')}</h1>
        <RecordingForm
          saving={saving}
          onSubmit={handleSubmit}
          recording={recording}
          values={values}
          submitLabel={i18n.t('save-track')}
        />
      </Container>
    )
  } else {
    return (
      <ErrorView />
    )
  }
}

EditRecordingView.propTypes = {
  recordingId: PropTypes.string
}

export default EditRecordingPage;